// front/src/components/common/IncrementosModal.js — VERSIÓN SIMPLIFICADA
import React, { useState, useEffect, useRef } from 'react';
import {
  Modal, Button, Badge, Spinner, Table, Form, Alert
} from 'react-bootstrap';
import * as XLSX from 'xlsx';
import api from '../../services/axiosConfig';
import Select from 'react-select';
import './IncrementosModal.css';

import {
  FiTrendingUp, FiBarChart2, FiDownload, FiPlus,
  FiTrash2, FiCheckCircle, FiX, FiAlertTriangle,
  FiCalendar, FiRefreshCw, FiDollarSign
} from 'react-icons/fi';

// ─── Utilidades ─────────────────────────────────────────────────────────────

const fmt = (v) =>
  new Intl.NumberFormat('es-EC', {
    style: 'currency', currency: 'USD', minimumFractionDigits: 2
  }).format(v ?? 0);


const hoy = () =>
  new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Mexico_City' }).format(new Date());

const primerDiaMes = () => `${hoy().slice(0, 8)}01`;

const TIPOS_DEPOSITO = [
  { value: 'stp',              label: 'STP' },
  { value: 'cuenta_proveedor', label: 'Cuenta bancaria proveedor' },
  { value: 'cuenta_personal',  label: 'Cuenta bancaria personal' },
  { value: 'inyeccion',        label: 'Inyección' },
];

const labelTipo = (v) =>
  TIPOS_DEPOSITO.find(t => t.value === v)?.label ?? v ?? '-';

// ─── Fila vacía para nuevo depósito ─────────────────────────────────────────

const filaVacia = () => ({
  _key: Date.now() + Math.random(),
  fecha: hoy(),
  usuarioId: '',
  monto: '',
  tipoDeposito: 'stp',
  notas: '',
  guardado: false,
  guardando: false,
  error: null,
});

// ─── Componente principal ────────────────────────────────────────────────────

const IncrementosModal = ({ show, handleClose, proveedor }) => {
  const [tab, setTab] = useState('incrementos');

  // Datos base
  const [incrementos, setIncrementos]       = useState([]);
  const [usuarios, setUsuarios]             = useState([]);
  const [loadingBase, setLoadingBase]       = useState(false);

  // Depósitos por incremento: { [incId]: Fila[] }
  const [filasMap, setFilasMap]             = useState({});

  // Reportes
  const [reporte, setReporte]               = useState(null);
  const [fechaInicio, setFechaInicio]       = useState(primerDiaMes());
   const [fechaFin, setFechaFin]             = useState(hoy());
  const [loadingReporte, setLoadingReporte] = useState(false);
  const [errorReporte, setErrorReporte]     = useState(null);
    const [paginaRecargas, setPaginaRecargas] = useState(1);

  // Métricas Movistar
  const [metricas, setMetricas]             = useState(null);
  const [simulacion, setSimulacion]         = useState(null);

  // Asignación en curso por incremento: { [incId]: boolean }
  const [asignandoMap, setAsignandoMap]     = useState({});

  // Toast
  const [toast, setToast]                   = useState(null);
  const toastTimer = useRef(null);

  // ── Efectos ──────────────────────────────────────────────────────────────

  useEffect(() => {
    if (show) {
      cargarBase();
    } else {
      resetear();
    }
  }, [show, proveedor]);

  // Si cambian las fechas o el proveedor, volver a la página 1
  useEffect(() => {
    setPaginaRecargas(1);
  }, [fechaInicio, fechaFin, proveedor]);

  // Espera 400 ms después del último cambio antes de pedir el reporte
  useEffect(() => {
    if (tab !== 'reportes' || !show) return;
    const t = setTimeout(() => cargarReporte(), 400);
    return () => clearTimeout(t);
  }, [tab, fechaInicio, fechaFin, show, paginaRecargas]);

  // ── Helpers de estado ────────────────────────────────────────────────────

  const resetear = () => {
    setTab('incrementos');
    setIncrementos([]);
    setFilasMap({});
    setReporte(null);
    setErrorReporte(null);
    setMetricas(null);
    setSimulacion(null);
    setFechaInicio(primerDiaMes());
    setFechaFin(hoy());      // ← nuevo
    setPaginaRecargas(1);
  };

  const showToast = (msg, tipo = 'success') => {
    clearTimeout(toastTimer.current);
    setToast({ msg, tipo });
    toastTimer.current = setTimeout(() => setToast(null), 4000);
  };

  const opcionesUsuarios = usuarios.map(u => ({
    value: u.id,
    label: u.displayName || u.nombres_apellidos || u.nombre_tienda || `#${u.id}`,
  }));

  // ── Carga inicial ────────────────────────────────────────────────────────

  const cargarBase = async () => {
    setLoadingBase(true);
    try {
      const [resInc, resUsr] = await Promise.all([
        api.get('/incrementos/notificaciones', { params: { proveedor } }),
        api.get('/admin/usuarios-validos').catch(() => api.get('/admin/usuarios')),
      ]);

      const incs = resInc.data.incrementos ?? [];
      setIncrementos(incs);
      setUsuarios(resUsr.data.usuarios ?? resUsr.data ?? []);

      // Inicializar filas: depósitos ya asignados + fila vacía
      const mapa = {};
      for (const inc of incs) {
        const depositosExistentes = (inc.depositos ?? []).map(d => ({
          _key:         d.id,
          fecha:        d.fecha?.split('T')[0] ?? hoy(),
          usuarioId:    d.usuarioId ?? '',
          monto:        String(d.monto),
          tipoDeposito: d.tipoDeposito ?? 'stp',
          notas:        d.notas ?? '',
          guardado:     true,
          guardando:    false,
          error:        null,
          _id:          d.id,
          _usuario:     d.Usuario,
        }));
        mapa[inc.id] = [...depositosExistentes, filaVacia()];
      }
      setFilasMap(mapa);

    } catch (e) {
      showToast('Error al cargar datos', 'error');
    } finally {
      setLoadingBase(false);
    }
  };

  // ── Manipulación de filas ────────────────────────────────────────────────

  const setFilas = (incId, fn) =>
    setFilasMap(prev => ({ ...prev, [incId]: fn(prev[incId] ?? []) }));

  const actualizarFila = (incId, key, campo, valor) =>
    setFilas(incId, filas =>
      filas.map(f => f._key === key ? { ...f, [campo]: valor, error: null } : f)
    );

  const agregarFila = (incId) =>
    setFilas(incId, filas => [...filas, filaVacia()]);

  const eliminarFila = async (incId, fila) => {
    if (fila.guardado && fila._id) {
      if (!window.confirm('¿Eliminar este depósito?')) return;
      try {
        await api.delete(`/incrementos/depositos/${fila._id}`);
        showToast('Depósito eliminado');
      } catch {
        showToast('Error al eliminar', 'error');
        return;
      }
    }
    setFilas(incId, filas => filas.filter(f => f._key !== fila._key));
  };

  const guardarFila = async (incId, fila) => {
    if (!fila.monto || !fila.usuarioId) {
      setFilas(incId, filas =>
        filas.map(f =>
          f._key === fila._key
            ? { ...f, error: 'Monto y usuario son obligatorios' }
            : f
        )
      );
      return;
    }

    // Optimistic: marcar como guardando
    setFilas(incId, filas =>
      filas.map(f => f._key === fila._key ? { ...f, guardando: true, error: null } : f)
    );

    try {
      const res = await api.post('/incrementos/depositos', {
        monto:        parseFloat(fila.monto),
        usuarioId:    parseInt(fila.usuarioId),
        tipoDeposito: fila.tipoDeposito,
        notas:        fila.notas,
        fecha:        fila.fecha,
        proveedor,
        incrementoId: incId,
      });

      const nuevo = res.data.deposito ?? res.data;

      setFilas(incId, filas =>
        filas.map(f =>
          f._key === fila._key
            ? { ...f, guardado: true, guardando: false, _id: nuevo.id }
            : f
        )
      );
      // Agregar nueva fila vacía al final
      setFilas(incId, filas => [...filas, filaVacia()]);
      showToast('Depósito guardado ✓');
    } catch (e) {
      setFilas(incId, filas =>
        filas.map(f =>
          f._key === fila._key
            ? {
                ...f,
                guardando: false,
                error: e.response?.data?.error ?? 'Error al guardar',
              }
            : f
        )
      );
    }
  };

  // ── Asignar depósitos guardados a un incremento ─────────────────────────

  const handleAsignar = async (inc) => {
    const filas = filasMap[inc.id] ?? [];
    const depositosGuardados = filas.filter(f => f.guardado && f._id);

    if (depositosGuardados.length === 0) {
      showToast('Primero guarda al menos un depósito', 'error');
      return;
    }

    setAsignandoMap(prev => ({ ...prev, [inc.id]: true }));
    try {
      const res = await api.post(
        `/incrementos/incrementos/${inc.id}/asignar`,
        { depositosIds: depositosGuardados.map(f => f._id) }
      );
      const { resumen } = res.data;
      showToast(
        `Asignado ✓  Ganancia: ${resumen?.ganancia ?? ''}  ${resumen?.porcentajeGanancia ? `(${resumen.porcentajeGanancia})` : ''}`
      );
      // Refrescar lista de incrementos
      await cargarBase();
    } catch (e) {
      showToast(e.response?.data?.error ?? 'Error al asignar', 'error');
    } finally {
      setAsignandoMap(prev => ({ ...prev, [inc.id]: false }));
    }
  };

  // ── Reporte ──────────────────────────────────────────────────────────────

  const cargarReporte = async () => {
    setLoadingReporte(true);
    setErrorReporte(null);
    try {
      const params = new URLSearchParams({ proveedor, fechaInicio, fechaFin, pagina: paginaRecargas });
            const res = await api.get(`/contabilidad/reporte-completo?${params}`);
      setReporte(res.data);

      const fechaMinima = res.data.limitesCalendario?.fechaMinima;
        if (fechaMinima && fechaInicio < fechaMinima) {
          setFechaInicio(fechaMinima);
        }

      if (proveedor === 'movistar') {
        const p = new URLSearchParams({ startDate: fechaInicio, endDate: fechaFin });
        const rm = await api.get(`/incrementos/movistar/porcentaje-real?${p}`);
        setMetricas(rm.data);

        if (rm.data && !rm.data.error) {
          const ps = new URLSearchParams({
            capitalInicial:    rm.data.capitalInicial,
            promedioRecarga:   (rm.data.totalInvertido / rm.data.cantidadRecargas).toFixed(2),
            porcentajeComision: rm.data.porcentajePeriodo,
            minimoOperable:    '20',
          });
          const rs = await api.get(`/incrementos/movistar/simulacion-rendimiento?${ps}`);
          setSimulacion(rs.data);
        }
      }
    } catch (e) {
      setErrorReporte(e.response?.data?.error ?? e.message);
    } finally {
      setLoadingReporte(false);
    }
  };

  const exportarExcel = async () => {
    if (!reporte) return;

    // La pantalla solo tiene las últimas 200; para el Excel se piden todas
    let recargasExcel = [];
    try {
      const p = new URLSearchParams({ proveedor, fechaInicio, fechaFin, todas: '1' });
      const r = await api.get(`/contabilidad/reporte-completo?${p}`);
      recargasExcel = r.data.detalles.recargas;
    } catch (e) {
      showToast('No se pudieron obtener las recargas para el Excel', 'error');
      return;
    }

    const wb = XLSX.utils.book_new();

    const wsRes = XLSX.utils.aoa_to_sheet([
      ['Reporte de Contabilidad — GestoPago'],
      ['Proveedor:', proveedor.toUpperCase()],
      ['Periodo:', `${fechaInicio} → ${fechaFin}`],
      [],
      ['Saldo Inicial:',    reporte.saldos.inicial],
      ['Saldo Final:',      reporte.saldos.finalReal],
      ['Total Incrementos:', reporte.totales.incrementos],
      ['Total Depositado:', reporte.totales.depositosAsignados],
      ['Total Recargado:',  reporte.totales.recargas],
      ...(proveedor === 'movistar'
        ? [['Comisiones:', reporte.totales.comisiones ?? 0]]
        : [['Ganancia Real:', reporte.totales.gananciaReal]]),
    ]);
    XLSX.utils.book_append_sheet(wb, wsRes, 'Resumen');

    if (reporte.detalles.incrementos.length) {
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.aoa_to_sheet([
          ['Fecha','Tipo','Incremento','Depósito','Ganancia','%','Estado'],
          ...reporte.detalles.incrementos.map(i => [
            i.fecha, i.tipo, i.incremento, i.depositosAsociados,
            i.ganancia, i.porcentaje, i.estado,
          ]),
        ]),
        'Incrementos'
      );
    }

    if (reporte.detalles.depositos.length) {
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.aoa_to_sheet([
          ['Fecha','Monto','Método','Usuario','Asignado'],
          ...reporte.detalles.depositos.map(d => [
            d.fecha, d.monto, labelTipo(d.tipoDeposito), d.usuario ?? '-',
            d.asignado ? 'Sí' : 'No',
          ]),
        ]),
        'Depósitos'
      );
    }

    if (recargasExcel.length) {
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.aoa_to_sheet([
          ['Fecha','Número','Operadora','Valor','Comisión','Saldo'],
          ...recargasExcel.map(r => [
            r.fecha, r.numeroRecarga ?? '-', r.operadora ?? '-',
            r.valor, r.comision, r.saldoNuevo ?? '-',
          ]),
        ]),
        'Recargas'
      );
    }

    XLSX.writeFile(wb, `Reporte_${proveedor}_${fechaInicio}_${fechaFin}.xlsx`);
    showToast('Excel exportado ✓');
  };  

  // ── Calcular ganancia en tiempo real por incremento ──────────────────────

  const gananciaIncremento = (inc) => {
    const filas = filasMap[inc.id] ?? [];
    const totalDep = filas
      .filter(f => f.guardado)
      .reduce((s, f) => s + parseFloat(f.monto || 0), 0);
    const ganancia = parseFloat(inc.diferencia) - totalDep;
    const pct = totalDep > 0 ? (ganancia / totalDep) * 100 : null;
    return { totalDep, ganancia, pct };
  };

  // ────────────────────────────────────────────────────────────────────────
  // RENDER
  // ────────────────────────────────────────────────────────────────────────

  const badge = proveedor === 'general'
    ? <Badge bg="primary">General (2611)</Badge>
    : <Badge bg="success">Movistar (2612)</Badge>;

  return (
    <>
      {/* Toast flotante */}
      {toast && (
        <div className="sim-toast" data-tipo={toast.tipo}>
          {toast.tipo === 'error' ? <FiAlertTriangle /> : <FiCheckCircle />}
          <span>{toast.msg}</span>
          <button onClick={() => setToast(null)}><FiX /></button>
        </div>
      )}

      <Modal show={show} onHide={handleClose} size="xl" centered className="sim-modal">
        <Modal.Header closeButton>
          <Modal.Title className="sim-modal-title">
            <FiBarChart2 className="me-2" />
            Contabilidad {badge}
          </Modal.Title>
        </Modal.Header>

        {/* Pestañas */}
        <div className="sim-tabs">
          <button
            className={`sim-tab ${tab === 'incrementos' ? 'active' : ''}`}
            onClick={() => setTab('incrementos')}
          >
            <FiTrendingUp />
            Incrementos
            {incrementos.length > 0 && (
              <span className="sim-tab-badge">{incrementos.length}</span>
            )}
          </button>
          <button
            className={`sim-tab ${tab === 'reportes' ? 'active' : ''}`}
            onClick={() => setTab('reportes')}
          >
            <FiBarChart2 />
            Reportes
          </button>
        </div>

        <Modal.Body className="sim-body">

          {/* ══════════ TAB INCREMENTOS ══════════ */}
          {tab === 'incrementos' && (
            loadingBase ? (
              <div className="sim-loading">
                <Spinner animation="border" />
                <span>Cargando...</span>
              </div>
            ) : incrementos.length === 0 ? (
              <div className="sim-empty">
                <FiCheckCircle size={40} />
                <p>No hay incrementos pendientes para <strong>{proveedor}</strong></p>
              </div>
            ) : (
              <div className="sim-incrementos-list">
                {incrementos.map(inc => {
                  const { totalDep, ganancia, pct } = gananciaIncremento(inc);
                  const filas = filasMap[inc.id] ?? [];

                  return (
                    <div key={inc.id} className="sim-inc-block">

                      {/* Cabecera del incremento */}
                      <div className="sim-inc-header">
                        <div className="sim-inc-info">
                          <span className="sim-inc-label">Incremento detectado</span>
                          <span className="sim-inc-monto">{fmt(inc.diferencia)}</span>
                          <span className="sim-inc-meta">
                            {inc.operadora} · {new Date(inc.fecha).toLocaleDateString('es-EC')}
                          </span>
                        </div>
                        <div className="sim-inc-resumen">
                          <div className="sim-kpi">
                            <span>Depositado</span>
                            <strong>{fmt(totalDep)}</strong>
                          </div>
                          <div className={`sim-kpi ${ganancia >= 0 ? 'positivo' : 'negativo'}`}>
                            <span>Ganancia</span>
                            <strong>
                              {fmt(ganancia)}
                              {pct !== null && (
                                <em> ({pct.toFixed(1)}%)</em>
                              )}
                            </strong>
                          </div>
                          <button
                            className="sim-btn-asignar"
                            onClick={() => handleAsignar(inc)}
                            disabled={
                              asignandoMap[inc.id] ||
                              (filasMap[inc.id] ?? []).filter(f => f.guardado).length === 0
                            }
                            title="Asignar los depósitos guardados a este incremento"
                          >
                            {asignandoMap[inc.id]
                              ? <Spinner animation="border" size="sm" />
                              : <><FiCheckCircle size={14} /> Asignar</>
                            }
                          </button>
                        </div>
                      </div>

                      {/* Tabla de depósitos */}
                      <div className="sim-table-wrap">
                        <table className="sim-table">
                          <thead>
                            <tr>
                              <th style={{ width: 130 }}>Fecha</th>
                              <th style={{ width: 220 }}>Usuario / Tienda</th>
                              <th style={{ width: 110 }}>Monto</th>
                              <th style={{ width: 50 }}></th>
                            </tr>
                          </thead>
                          <tbody>
                            {filas.map(fila => (
                              <FilaDeposito
                                key={fila._key}
                                fila={fila}
                                opcionesUsuarios={opcionesUsuarios}
                                onChange={(campo, valor) =>
                                  actualizarFila(inc.id, fila._key, campo, valor)
                                }
                                onGuardar={() => guardarFila(inc.id, fila)}
                                onEliminar={() => eliminarFila(inc.id, fila)}
                              />
                            ))}
                          </tbody>
                        </table>

                        <button
                          className="sim-add-row"
                          onClick={() => agregarFila(inc.id)}
                        >
                          <FiPlus /> Agregar fila
                        </button>
                      </div>

                    </div>
                  );
                })}
              </div>
            )
          )}

          {/* ══════════ TAB REPORTES ══════════ */}
          {tab === 'reportes' && (
            <div className="sim-reportes">

              {/* Filtros de fecha */}
              <div className="sim-date-row">
                <div className="sim-date-group">
                  <label><FiCalendar /> Desde</label>
                  <input
                    type="date"
                    value={fechaInicio}
                    min={reporte?.limitesCalendario?.fechaMinima}   // ← nuevo
                    max={fechaFin}
                    onChange={e => setFechaInicio(e.target.value)}
                  />
                </div>
                <div className="sim-date-group">
                  <label><FiCalendar /> Hasta</label>
                  <input
                    type="date"
                    value={fechaFin}
                    min={fechaInicio}
                    max={hoy()}
                    onChange={e => setFechaFin(e.target.value)}
                  />
                </div>
                <button
                  className="sim-btn sim-btn-outline"
                  onClick={cargarReporte}
                  disabled={loadingReporte}
                >
                  <FiRefreshCw /> Actualizar
                </button>
                <button
                  className="sim-btn sim-btn-primary"
                  onClick={exportarExcel}
                  disabled={!reporte || loadingReporte}
                >
                  <FiDownload /> Exportar Excel
                </button>
              </div>

              {errorReporte && (
                <div className="sim-error-box">
                  <FiAlertTriangle /> {errorReporte}
                </div>
              )}

              {reporte && !loadingReporte && reporte.pendientes?.cantidad > 0 && (
                <Alert variant="warning" className="mb-2">
                  <FiAlertTriangle /> Hay {reporte.pendientes.cantidad} incremento(s) por{' '}
                  {fmt(reporte.pendientes.monto)} sin asignar. La ganancia mostrada no los incluye
                  hasta que asignes sus depósitos.
                </Alert>
              )}

              {loadingReporte && (
                <div className="sim-loading">
                  <Spinner animation="border" />
                  <span>Generando reporte...</span>
                </div>
              )}

              {reporte && (
                <>
                  {/* KPIs principales */}
                  <div className="sim-kpi-row">
                    <div className="sim-kpi-card">
                      <span>Saldo Actual</span>
                      <strong>{fmt(reporte.saldos.finalReal)}</strong>
                    </div>
                    <div className="sim-kpi-card">
                      <span>Incrementos</span>
                      <strong className="verde">{fmt(reporte.totales.incrementos)}</strong>
                    </div>
                    <div className="sim-kpi-card">
                      <span>Depositado</span>
                      <strong>{fmt(reporte.totales.depositosAsignados)}</strong>
                    </div>
                    <div className="sim-kpi-card">
                      <span>Recargado</span>
                      <strong className="rojo">-{fmt(reporte.totales.recargas)}</strong>
                    </div>
                    {proveedor === 'movistar' ? (
                      <div className="sim-kpi-card destacado">
                        <span>Comisiones ganadas</span>
                        <strong className="verde">
                          {metricas && !metricas.error
                            ? `${metricas.porcentajePeriodoRedondeado}% · ${fmt(metricas.totalComisionesReales)}`
                            : fmt(reporte.totales.comisiones)}
                        </strong>
                      </div>
                    ) : (
                      <div className="sim-kpi-card destacado">
                        <span>Ganancia (asignada)</span>
                        <strong className="verde">
                          {reporte.totales.incrementos === 0
                            ? '—'
                            : fmt(reporte.totales.gananciaReal)}
                        </strong>
                      </div>
                    )}
                    <div className="sim-kpi-card">
                      <span>Recargas</span>
                      <strong>{reporte.contadores.recargas}</strong>
                    </div>
                  </div>

                  {/* Proyección Movistar */}
                  {proveedor === 'movistar' && simulacion && !simulacion.error && (
                    <div className="sim-proyeccion">
                      <h6><FiBarChart2 /> Proyección: si usas todo el capital</h6>
                      <div className="sim-proy-grid">
                        <div>
                          <span>Capital disponible</span>
                          <strong>{fmt(metricas?.saldoFinal)}</strong>
                        </div>
                        <div>
                          <span>Recargas estimadas</span>
                          <strong>{simulacion.numeroRecargas}</strong>
                        </div>
                        <div>
                          <span>Ganancia potencial</span>
                          <strong className="verde">{fmt(simulacion.totalComisionesGanadas)}</strong>
                        </div>
                        <div>
                          <span>Rendimiento efectivo</span>
                          <strong className="verde">{simulacion.rendimientoEfectivoRedondeado}%</strong>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Tablas detalle */}
                  <ReporteTablas
                    reporte={reporte} fmt={fmt} labelTipo={labelTipo}
                    pagina={paginaRecargas} setPagina={setPaginaRecargas}
                    cargando={loadingReporte}
                  />                </>
              )}
            </div>
          )}

        </Modal.Body>

        <Modal.Footer className="sim-footer">
          <button className="sim-btn sim-btn-ghost" onClick={handleClose}>
            Cerrar
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

// ─── FilaDeposito ────────────────────────────────────────────────────────────

const FilaDeposito = ({ fila, opcionesUsuarios, onChange, onGuardar, onEliminar }) => {
  const inputRef = useRef(null);

  // Si la fila se acaba de crear, enfocar el campo de monto
  useEffect(() => {
    if (!fila.guardado) {
      inputRef.current?.focus();
    }
  }, []);

  if (fila.guardado) {
    // Fila guardada: lectura
    const usuario = opcionesUsuarios.find(o => o.value === fila.usuarioId);
    return (
      <tr className="sim-row-saved">
        <td>{fila.fecha}</td>
        <td>{usuario?.label ?? fila._usuario?.nombres_apellidos ?? fila._usuario?.nombre_tienda ?? '—'}</td>
        <td className="verde fw-bold">{parseFloat(fila.monto || 0).toLocaleString('es-EC', { minimumFractionDigits: 2 })}</td>
        <td>
          <button className="sim-icon-btn danger" onClick={onEliminar} title="Eliminar">
            <FiTrash2 size={14} />
          </button>
        </td>
      </tr>
    );
  }

  // Fila en edición
  return (
    <>
      <tr className={`sim-row-edit ${fila.error ? 'has-error' : ''}`}>
        <td>
          <input
            type="date"
            value={fila.fecha}
            max={hoy()}
            onChange={e => onChange('fecha', e.target.value)}
            className="sim-input"
          />
        </td>
        <td>
          <Select
            options={opcionesUsuarios}
            value={opcionesUsuarios.find(o => o.value === fila.usuarioId) ?? null}
            onChange={opt => onChange('usuarioId', opt?.value ?? '')}
            placeholder="Buscar..."
            isClearable
            isSearchable
            menuPortalTarget={document.body}
            styles={{
              control: b => ({ ...b, minHeight: 32, fontSize: 13 }),
              menuPortal: b => ({ ...b, zIndex: 9999 }),
            }}
          />
        </td>
        <td>
          <input
            ref={inputRef}
            type="number"
            step="0.01"
            min="0"
            placeholder="0.00"
            value={fila.monto}
            onChange={e => onChange('monto', e.target.value)}
            onKeyDown={e => e.key === 'Enter' && onGuardar()}
            className="sim-input text-end"
          />
        </td>

        <td>
          <button
            className="sim-icon-btn success"
            onClick={onGuardar}
            disabled={fila.guardando}
            title="Guardar"
          >
            {fila.guardando ? <Spinner size="sm" animation="border" /> : <FiCheckCircle size={14} />}
          </button>
        </td>
      </tr>
      {fila.error && (
        <tr className="sim-row-error">
          <td colSpan={6}>
            <span><FiAlertTriangle size={12} /> {fila.error}</span>
          </td>
        </tr>
      )}
    </>
  );
};

// ─── Tablas detalle del reporte ───────────────────────────────────────────────

const ReporteTablas = ({ reporte, fmt, labelTipo, pagina, setPagina, cargando }) => {
  const totalPaginas = reporte.paginacion?.totalPaginas ?? 1;
  const porPagina = reporte.paginacion?.porPagina ?? 200;
  const desde = reporte.contadores.recargas === 0 ? 0 : (pagina - 1) * porPagina + 1;
  const hasta = Math.min(pagina * porPagina, reporte.contadores.recargas);
  const [subtab, setSubtab] = useState('incrementos');

  const tabs = [
    { key: 'incrementos', label: 'Incrementos', count: reporte.detalles.incrementos.length },
    { key: 'depositos',   label: 'Depósitos',   count: reporte.detalles.depositos.length },
    { key: 'recargas',    label: 'Recargas',     count: reporte.contadores.recargas },
    ];

  return (
    <div className="sim-detail-block">
      <div className="sim-subtabs">
        {tabs.map(t => (
          <button
            key={t.key}
            className={`sim-subtab ${subtab === t.key ? 'active' : ''}`}
            onClick={() => setSubtab(t.key)}
          >
            {t.label}
            <span className="sim-subtab-count">{t.count}</span>
          </button>
        ))}
      </div>

      <div className="sim-detail-table">
        {subtab === 'incrementos' && (
          reporte.detalles.incrementos.length === 0
            ? <p className="sim-empty-row">Sin incrementos en este periodo</p>
            : <table className="sim-table">
                <thead>
                  <tr>
                    <th>Fecha</th><th>Tipo</th><th>Incremento</th>
                    <th>Depósito</th><th>Ganancia</th><th>%</th><th>Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {reporte.detalles.incrementos.map(i => (
                    <tr key={i.id}>
                      <td>{i.fecha}</td>
                      <td><span className="sim-badge secondary">{i.tipo.replace('_', ' ')}</span></td>
                      <td className="verde">{fmt(i.incremento)}</td>
                      <td>{fmt(i.depositosAsociados)}</td>
                      <td className="verde">{i.estado === 'pendiente' ? 'Pendiente' : fmt(i.ganancia)}</td>
                      <td>{i.estado === 'pendiente' ? '—' : `${i.porcentaje.toFixed(1)}%`}</td>

                      <td>
                        <span className={`sim-badge ${i.estado === 'asignado' ? 'success' : 'warning'}`}>
                          {i.estado}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
        )}

        {subtab === 'depositos' && (
          reporte.detalles.depositos.length === 0
            ? <p className="sim-empty-row">Sin depósitos en este periodo</p>
            : <table className="sim-table">
                <thead>
                  <tr>
                    <th>Fecha</th><th>Monto</th>
                    <th>Usuario</th><th>Asignado</th>
                  </tr>
                </thead>
                <tbody>
                  {reporte.detalles.depositos.map(d => (
                    <tr key={d.id}>
                      <td>{d.fecha}</td>
                      <td className="fw-bold">{fmt(d.monto)}</td>
                      <td>{d.usuario || '—'}</td>
                      <td>
                        <span className={`sim-badge ${d.asignado ? 'success' : 'warning'}`}>
                          {d.asignado ? 'Sí' : 'No'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
        )}

        {subtab === 'recargas' && (
          reporte.detalles.recargas.length === 0
            ? <p className="sim-empty-row">Sin recargas en este periodo</p>
            : <>
              {totalPaginas > 1 && (
                <div className="d-flex justify-content-center align-items-centergap-3 my-2">
                  <button
                    className="sim-btn sim-btn-outline"
                    disabled={pagina <= 1 || cargando}
                    onClick={() => setPagina(pagina - 1)}
                  >
                    ← Anteriores
                  </button>
                  <span>
                    {desde}–{hasta} de {reporte.contadores.recargas} · Página {pagina} de {totalPaginas}
                  </span>
                  <button
                    className="sim-btn sim-btn-outline"
                    disabled={pagina >= totalPaginas || cargando}
                    onClick={() => setPagina(pagina + 1)}
                  >
                    Siguientes →
                  </button>
                </div>
              )}
              <table className="sim-table">
                <thead>
                  <tr>
                    <th>Fecha</th><th>Número</th><th>Operadora</th>
                    <th>Valor</th><th>Comisión</th><th>Saldo</th>
                  </tr>
                </thead>
                <tbody>
                  {reporte.detalles.recargas.map((r, i) => (
                    <tr key={i}>
                      <td>{r.fecha}</td>
                      <td>{r.numeroRecarga || '—'}</td>
                      <td>{r.operadora || '—'}</td>
                      <td className="rojo">-{fmt(r.valor)}</td>
                      <td className="verde">+{fmt(r.comision)}</td>
                      <td>{r.saldoNuevo ? fmt(r.saldoNuevo) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
        )}
      </div>
    </div>
  );
};

export default IncrementosModal;